import { Card, CardActions, CardContent, Divider, Stack } from '@mui/material';
import type { RouteComponentProps } from '@reach/router';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import { FormikTextField } from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton/index';

import useCommonSchemas from '@hooks/schemas/useCommonSchemas';

import { useAdminCreatePermission } from 'generated/admin.generated';
import type { PermissionScope } from 'generated/types';

const messages = defineMessages({
  name: {
    id: 'admin.permissions.name',
    defaultMessage: 'Name',
  },
  scope: {
    id: 'admin.permissions.scope',
    defaultMessage: 'Scope',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  title: {
    id: 'admin.permissions.create.title',
    defaultMessage: 'New Permission',
  },
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
});

type FormValues = {
  name: string;
  scope: string;
};

const NewPermissionForm: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();
  const [createPermission, { loading: createPermissionLoading }] =
    useAdminCreatePermission();
  const { AdminSchema } = useCommonSchemas();

  const loading = createPermissionLoading;

  const validationSchema: Yup.Schema<FormValues> = Yup.object().shape({
    name: AdminSchema.name().required(formatMessage(messages.required)),
    scope: AdminSchema.permissionScope.required(
      formatMessage(messages.required)
    ),
  });

  const initialValues: FormValues = {
    name: '',
    scope: '',
  };

  async function handleSubmit(
    values: FormValues,
    { resetForm }: FormikHelpers<FormValues>
  ) {
    await createPermission({
      variables: {
        input: {
          permission_name: values.name,
          permission_scope: values.scope as PermissionScope,
        },
      },
    });
    resetForm();
  }

  return (
    <Card>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting }) => (
          <Form>
            <CardContent>
              <Stack direction="row" spacing={2}>
                <FormikTextField
                  autoComplete="off"
                  label={formatMessage(messages.name)}
                  name="name"
                  fullWidth
                />
                <FormikTextField
                  autoComplete="off"
                  label={formatMessage(messages.scope)}
                  name="scope"
                  fullWidth
                />
              </Stack>
            </CardContent>
            <Divider />
            <CardActions>
              <RoundedButton disabled={isSubmitting || loading} type="submit">
                {formatMessage(messages.submit)}
              </RoundedButton>
            </CardActions>
          </Form>
        )}
      </Formik>
    </Card>
  );
};

export default NewPermissionForm;
